package com.schoolconnect.app.feature.home

import com.schoolconnect.app.feature.rolepicker.RolePickerUiState
import com.schoolconnect.app.datasource.network.response.DashboardAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.DashboardStatisticsResponse
import com.schoolconnect.app.datasource.network.response.DashboardSchoolsAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.EventsNoticesAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.RecentActivitiesResponse
import com.schoolconnect.app.datasource.network.response.SystemHealthResponse
import com.schoolconnect.app.datasource.network.response.SchoolAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.SchoolStatisticsResponse
import com.schoolconnect.app.datasource.network.response.TeacherDashboardOverviewResponse
import com.schoolconnect.app.datasource.network.response.ParentListResponse
import com.schoolconnect.app.datasource.network.response.StudentResponse
import com.schoolconnect.app.datasource.network.response.ParentDashboardResponse
import com.schoolconnect.app.datasource.network.response.QuickAnnouncementResponse

data class HomeUiState(
    val roleCode: String = "",
    val roleLabel: String = "Super Admin",
    val roleSubtitle: String = "Super Administrator",
    val schoolLabel: String = "iConnect.School",
    val schoolId: String = "",
    val schoolCode: String = "",
    val schoolLogoUrl: String = "",
    val schoolLogoBytes: ByteArray? = null,
    val isSchoolLogoLoading: Boolean = false,
    val activeAcademicYearId: String = "",
    val activeAcademicYearLabel: String = "",
    /** Changes on every rail activation, including clicking the active item. */
    val railActivationKey: Long = 0L,
    val isActiveAcademicYearLoading: Boolean = false,
    val selectedNavId: String = "dashboard",
    val isAccountMenuExpanded: Boolean = false,
    val isSidebarCollapsed: Boolean = false,
    /** Metric cards / grid — not blocked by activity or health loads. */
    val isDashboardLoading: Boolean = false,
    /** Recent activity + system health panel. */
    val isDashboardFeedLoading: Boolean = false,
    val isSchoolDashboardLoading: Boolean = false,
    val isSchoolStatisticsLoading: Boolean = false,
    val isSchoolDashboardEventsNoticesLoading: Boolean = false,
    val dashboardAnalytics: DashboardAnalyticsResponse? = null,
    val dashboardSchoolsAnalytics: DashboardSchoolsAnalyticsResponse? = null,
    val dashboardStatistics: DashboardStatisticsResponse? = null,
    val schoolDashboardAnalytics: SchoolAnalyticsResponse? = null,
    val schoolStatistics: SchoolStatisticsResponse? = null,
    val schoolDashboardEventsNotices: EventsNoticesAnalyticsResponse? = null,
    val recentActivities: RecentActivitiesResponse? = null,
    val systemHealth: SystemHealthResponse? = null,
    val teacherDashboard: TeacherDashboardOverviewResponse? = null,
    val isTeacherDashboardLoading: Boolean = false,
    val parentDashboard: ParentDashboardResponse? = null,
    val parentProfile: ParentListResponse? = null,
    val parentWards: List<StudentResponse> = emptyList(),
    val isParentDashboardLoading: Boolean = false,
    val isParentProfileLoading: Boolean = false,
    val activeAnnouncements: List<QuickAnnouncementResponse> = emptyList(),
    val isActiveAnnouncementsLoading: Boolean = false,
    val userProfile: HomeUserProfileUiState = HomeUserProfileUiState(),
    val rolePicker: RolePickerUiState = HomeDefaults.createRolePickerState()
)

data class HomeUserProfileUiState(
    val fullName: String = "",
    val firstName: String = "",
    val middleName: String = "",
    val lastName: String = "",
    val mobileNumber: String = "",
    val email: String = "",
    val employeeId: String = "",
    val schoolRole: String = "",
    val authRole: String = "",
    val lastLoginLabel: String = "",
    val mobileVerified: Boolean? = null,
    val emailVerified: Boolean? = null,
    val profileImageUrl: String = "",
    val pushNotificationsEnabled: Boolean = true,
    val emailNotificationsEnabled: Boolean = true,
    val isLoading: Boolean = true
)

sealed interface HomeUiEvent {
    data object AccountMenuToggled : HomeUiEvent
    data object AccountMenuDismissed : HomeUiEvent
    data object RolePickerRequested : HomeUiEvent
    data class NavSelected(val navId: String) : HomeUiEvent
    data object SidebarCollapseToggled : HomeUiEvent
    data class RolePickerSelectionChanged(val optionId: String) : HomeUiEvent
    data class RolePickerRememberChanged(val value: Boolean) : HomeUiEvent
    data object RolePickerClosed : HomeUiEvent
    data object RolePickerContinueClicked : HomeUiEvent
    data class ProfileSaved(val values: EditProfileFormValues) : HomeUiEvent
}
